// ######### Imports
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from "react-router-dom";
import axios from 'axios';
import OrderMgmt from "./OrderMgmt";
import Autisti from "./Autisti";
import AddProduct from "./AddProduct";
import AddCorr from "./AddCorr";
import AddBollereso from "./AddBollereso";
import OrdersEdit from "./OrdersEdit";
import CorrsEdit from "./CorrsEdit";
import BolleresoEdit from "./BolleresoEdit";
import EditProductsWrapper from "./EditProductsWrapper";
import CorrsEditProductsWrapper from "./CorrsEditProductsWrapper";
import BolleresoEditProductsWrapper from "./BolleresoEditProductsWrapper";
import UserManagement from "./UserManagement";
import Login from "./Login";
import GlobalStyle from './GlobalStyle';
import Bolle from "./Bolle";
import Corrispettivi from "./Corrispettivi";
import CRM from "./CRM";
import PastUnfulfilledAlert from "./PastUnfulfilledAlert";
import { FaBars, FaTimes } from "react-icons/fa";
import { jwtDecode } from 'jwt-decode';

// ########### Styles and components
const LogoutButton = styled.button`
  width: 100%;
  padding: 15px;
  font-size: 18px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-align: center;
  margin-top: 10px;

  &:hover {
    background-color: #c0392b;
  }
`;
const Container = styled.div`
  display: flex;
  height: 100vh;
  background-color: #f4f4f4;
`;
const SidebarWrapper = styled.div`
  width: 250px;
  background-color: #2c3e50;
  color: white;
  display: flex;
  flex-direction: column;
  height: 100vh;
  box-sizing: border-box;
  padding: 20px;
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    transform: ${({ isOpen }) => (isOpen ? "translateX(0)" : "translateX(-100%)")};
    transition: transform 0.3s ease-in-out;
  }
`;
const SidebarLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 15px;
  font-size: 18px;
  border-radius: 5px;
  margin-bottom: 10px;
  background-color: #34495e;
  text-align: center;
  &:hover {
    background-color: #1abc9c;
  }
`;
const Content = styled.div`
  flex-grow: 1;
  padding: 2rem;
  background-color: white;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  @media (min-width: 768px) {
    padding: 3rem;
  }
  @media (min-width: 1400px) {
    margin: auto;
    max-width: 1400px;
  }
`;
const SidebarTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 500;
  color: #2c2c2c;
  background-color: #fff9c4;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const DayOfYear = styled.div`
  font-size: 0.9rem;
  color: #bdc3c7;
  margin-bottom: 1.5rem;
`;
const HamburgerButton = styled.button`
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1100;
  background: none;
  border: none;
  color: #2c3e50;
  font-size: 1.8rem;
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const TokenOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TokenModal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px 40px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  max-width: 360px;
  width: 90%;
`;

const TokenOkBtn = styled.button`
  margin-top: 20px;
  padding: 10px 32px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #c0392b; }
`;

// Giorno sequenziale dell'anno
function getDayOfYear(date = new Date()) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start + ((start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000);
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

// Main function
function App() {
const [userRole, setUserRole] = useState(null);
const [isAuthenticated, setIsAuthenticated] = useState(false);
const [menuOpen, setMenuOpen] = useState(false);
const [tokenScaduto, setTokenScaduto] = useState(false);
const dayOfYear = getDayOfYear();

useEffect(() => {
  const token = localStorage.getItem("token");
  if (token) {
    setIsAuthenticated(true);
    const decoded = jwtDecode(token);
    setUserRole(decoded.role);
  }
}, []);

useEffect(() => {
  const id = axios.interceptors.response.use(
    r => r,
    err => {
      const status = err.response?.status;
      const isLoginCall = err.config?.url?.includes("/api/login");
      const isAdminRestriction = err.response?.data?.message?.includes("amministratori");
      if ((status === 401 || (status === 403 && !isAdminRestriction)) && !isLoginCall) {
        setTokenScaduto(true);
      }
      return Promise.reject(err);
    }
  );
  return () => axios.interceptors.response.eject(id);
}, []);

 const handleLogin = () => {
  const token = localStorage.getItem("token");
  if (token) {
    const decoded = jwtDecode(token);
    setUserRole(decoded.role);
  }
  setIsAuthenticated(true);
 };
 const handleLogout = () => {
  localStorage.removeItem("token");
  setIsAuthenticated(false);
  setUserRole(null);
 };
 if (!isAuthenticated) {
  return <Login onLogin={handleLogin} />;
 }

// Return
return (
 <Router>
  <GlobalStyle />
  {tokenScaduto && (
    <TokenOverlay>
      <TokenModal>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: "#2c3e50" }}>Sessione scaduta</div>
        <div style={{ fontSize: 14, color: "#666" }}>Il token è scaduto, procedi al login.</div>
        <TokenOkBtn onClick={() => { setTokenScaduto(false); handleLogout(); }}>OK</TokenOkBtn>
      </TokenModal>
    </TokenOverlay>
  )}
   {userRole === 'admin' && <PastUnfulfilledAlert />}
   <HamburgerButton onClick={() => setMenuOpen(!menuOpen)}>
    {menuOpen ? <FaTimes /> : <FaBars />}
   </HamburgerButton>
    <Container>
     <SidebarWrapper isOpen={menuOpen} onClick={() => setMenuOpen(false)}>
      <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
       <SidebarTitle>GRV Portal</SidebarTitle>
       <DayOfYear>Giorno: {dayOfYear}</DayOfYear>
       <SidebarLink to="/OrderMgmt">📦 Ordini</SidebarLink>
       {userRole === 'admin' && <SidebarLink to="/autisti">🚚 Autisti</SidebarLink>}
       {userRole === 'admin' && <SidebarLink to="/users">👥 Utenti</SidebarLink>}
       <SidebarLink to="/bolle">📄 Bolle</SidebarLink>
       <SidebarLink to="/corrispettivi">📊 Corrispettivi</SidebarLink>
       {userRole === 'admin' && <SidebarLink to="/crm">📋 CRM</SidebarLink>}
       <SidebarLink as="button" onClick={(e) => { e.stopPropagation(); handleLogout(); }}
        style={{ backgroundColor: "#e74c3c", border: "none", cursor: "pointer", textAlign: "center", }} > 🚪 Logout
       </SidebarLink>
      </div>
     </SidebarWrapper>
     <Content>
      <Routes>
       <Route path="/" element={<OrderMgmt />} />
       <Route path="/OrderMgmt" element={<OrderMgmt />} />
       <Route path="/autisti" element={userRole === 'admin' ? <Autisti /> : <Navigate to="/OrderMgmt" replace />} />
       <Route path="/add-product/:orderId" element={<AddProduct />} />
       <Route path="/add-corr/:orderId" element={<AddCorr />} />
       <Route path="/add-bollereso/:orderId" element={<AddBollereso />} />
       <Route path="/ordersedit" element={<OrdersEdit />} />
       <Route path="/corrsedit" element={<CorrsEdit />} />
       <Route path="/bolleresoedit" element={<BolleresoEdit />} />
       <Route path="/edit-products/:orderId" element={<EditProductsWrapper />} />
       <Route path="/corrs-edit-products/:orderId" element={<CorrsEditProductsWrapper />} />
       <Route path="/bollereso-edit-products/:orderId" element={<BolleresoEditProductsWrapper />} />
       <Route path="/users" element={<UserManagement userRole={userRole} />} />
       <Route path="/bolle" element={<Bolle />} />
       <Route path="/corrispettivi" element={<Corrispettivi />} />
       <Route path="/crm" element={userRole === 'admin' ? <CRM /> : <Navigate to="/OrderMgmt" replace />} />
      </Routes>
     </Content>
    </Container>
  </Router>
);
}

export default App;
